import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, API, authQuery, downloadUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Trash2, X, BookOpen, Download, Search, Shield, Headphones, Loader2 } from "lucide-react";
import FileInputLocalized from "../components/FileInputLocalized";

const CAT_KEY = {
  rituri_grade: "library.cat_rituri_grade",
  simbolistica: "library.cat_simbolistica",
  istorie: "library.cat_istorie",
  ritualuri: "library.cat_ritualuri",
  filosofie: "library.cat_filosofie",
  manuale: "library.cat_manuale",
};

const CATEGORIES = ["rituri_grade", "simbolistica", "istorie", "ritualuri", "filosofie", "manuale"];

const emptyForm = {
  title: "",
  category: "rituri_grade",
  description: "",
  author: "",
  file: null,
};

export default function LibraryPage() {
  const { t, i18n } = useTranslation();
  const en = i18n.language !== "ro";
  const labels = Object.fromEntries(CATEGORIES.map((c) => [c, t(CAT_KEY[c])]));
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [books, setBooks] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [generatingAudioId, setGeneratingAudioId] = useState(null);

  const GRADE_LABELS = {
    1: t("library.grade_label_1"),
    2: t("library.grade_label_2"),
    3: t("library.grade_label_3"),
  };

  // REAA 1° → 33°. Craft lodges use only 1-3; higher degrees are only
  // available inside Scottish Rite bodies but we want the admin to be
  // able to tag a book for any degree (e.g. 18° Rose-Croix, 32° SPRS,
  // 33° SGIG). Names come from t("library.degree_N") so they auto-translate.
  const DEGREE_NAMES = [null, ...Array.from({ length: 33 }, (_, i) => t(`library.degree_${i + 1}`))];

  const updateGrade = async (book, newGrade) => {
    try {
      await api.patch(`/admin/library/${book.id}/min-grade`, { min_grade: newGrade });
      toast.success(t("library.grade_updated"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const audioInputRef = React.useRef(null);
  const [audioForBook, setAudioForBook] = React.useState(null);

  const pickAudio = (book) => {
    setAudioForBook(book);
    setTimeout(() => audioInputRef.current?.click(), 50);
  };

  const uploadAudio = async (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    const book = audioForBook;
    if (!f || !book) return;
    setGeneratingAudioId(book.id);
    const label = t("library.uploading_audio");
    const tid = toast.loading(`${label} 0%`);
    try {
      const CHUNK_SIZE = 8 * 1024 * 1024;
      const totalChunks = Math.max(1, Math.ceil((f.size || 0) / CHUNK_SIZE));
      const { data: start } = await api.post(`/admin/library/${book.id}/upload-audio/start`, {
        filename: f.name, total_chunks: totalChunks, total_size: f.size || 0,
      });
      for (let i = 0; i < totalChunks; i++) {
        const fd = new FormData();
        fd.append("chunk_index", String(i));
        fd.append("chunk", f.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await api.post(`/library/chunked/${start.upload_id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.loading(`${label} ${Math.round(((i + 1) / totalChunks) * 95)}%`, { id: tid });
      }
      await api.post(`/admin/library/upload-audio/${start.upload_id}/finish`);
      toast.dismiss(tid);
      toast.success(t("library.audio_uploaded"));
      await load();
    } catch (err) {
      toast.dismiss(tid);
      toast.error(formatApiError(err));
    } finally {
      setGeneratingAudioId(null);
      setAudioForBook(null);
    }
  };

  const deleteAudio = async (book) => {
    if (!window.confirm(t("library.delete_audio_confirm", { title: book.title }))) return;
    try {
      await api.delete(`/admin/library/${book.id}/audio`);
      toast.success(t("library.audio_deleted"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/library");
      setBooks(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = books;
    if (activeCat !== "all") rows = rows.filter((b) => b.category === activeCat);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter((b) =>
        (b.title || "").toLowerCase().includes(q) ||
        (b.author || "").toLowerCase().includes(q) ||
        (b.description || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [books, activeCat, query]);

  const counts = useMemo(() => {
    const out = { all: books.length };
    for (const c of CATEGORIES) out[c] = books.filter((b) => b.category === c).length;
    return out;
  }, [books]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) {
      toast.error(t("library.choose_a_pdf_file"));
      return;
    }
    setBusy(true);
    try {
      const fileSize = form.file.size || 0;
      // Chunked upload tuned for THROUGHPUT, not just safety:
      //   • 8 MB chunks → for a 51 MB book, only 7 round-trips (instead
      //     of 26 with 2 MB chunks). HTTP latency is the bottleneck on
      //     mobile/HTTPS, so fewer-and-larger > many-and-tiny.
      //   • 3-way concurrency → upload 3 chunks in parallel; each one
      //     still individually small enough to escape ingress 502 limits.
      // For a typical 50-100 MB ritual PDF this brings the upload time
      // from 30+ minutes down to ~1 minute on a normal connection.
      const CHUNK_SIZE = 8 * 1024 * 1024;       // 8 MB per chunk
      const PARALLEL = 3;                        // 3 concurrent chunk uploads
      const totalChunks = Math.max(1, Math.ceil(fileSize / CHUNK_SIZE));

      // Robust POST helper: 5 attempts with exponential backoff (1s/2s/4s/8s/8s),
      // only retries on 5xx / network errors. Client errors (4xx) fail fast.
      const postWithRetry = async (path, payload, opts = {}) => {
        let lastErr = null;
        for (let attempt = 1; attempt <= 5; attempt++) {
          try {
            const { data } = await api.post(path, payload, opts);
            return data;
          } catch (err) {
            lastErr = err;
            const status = err?.response?.status;
            if (status && status >= 400 && status < 500) throw err;
            if (attempt < 5) {
              await new Promise((r) => setTimeout(r, Math.min(8000, attempt * 1000)));
            }
          }
        }
        throw lastErr;
      };

      setProgress(0);
      setProgressLabel(t("library.preparing_upload"));
      const started = await postWithRetry("/library/chunked/start", {
        filename: form.file.name,
        title: form.title,
        category: form.category,
        description: form.description || "",
        author: form.author || "",
        total_chunks: totalChunks,
        total_size: fileSize,
      });
      const uploadId = started.upload_id;

      // Upload chunks with bounded concurrency. We track how many have
      // completed via a shared counter so the progress bar updates as
      // chunks land in any order.
      let completed = 0;
      const uploadChunk = async (i) => {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, fileSize);
        const blob = form.file.slice(start, end);
        const fd = new FormData();
        fd.append("chunk", blob, `chunk_${i}`);
        fd.append("chunk_index", String(i));
        await postWithRetry(`/library/chunked/${uploadId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        completed += 1;
        const pct = Math.round((completed / totalChunks) * 100);
        setProgress(pct);
        setProgressLabel((t("library.uploading")) + ` ${pct}%`);
      };

      // Simple parallel pool: walks the chunk index list and spins up
      // PARALLEL worker promises that each pick the next pending chunk.
      let next = 0;
      const workers = [];
      for (let w = 0; w < Math.min(PARALLEL, totalChunks); w++) {
        workers.push((async () => {
          while (true) {
            const idx = next++;
            if (idx >= totalChunks) return;
            await uploadChunk(idx);
          }
        })());
      }
      await Promise.all(workers);

      setProgressLabel(t("library.finalizing"));
      await postWithRetry(`/library/chunked/${uploadId}/finish`);
      toast.success(t("library.book_added"));
      setShowForm(false);
      setForm(emptyForm);
      setProgress(0);
      setProgressLabel("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(en ? `Delete book "${b.title}"?` : `Ștergi cartea "${b.title}"?`)) return;
    try {
      await api.delete(`/library/${b.id}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDownload = (b) => {
    const url = `${API}/library/${b.id}/download${authQuery()}`;
    downloadUrl(url, b.filename);
  };

  return (
    <div data-testid="library-page">
      <input
        type="file"
        ref={audioInputRef}
        accept=".mp3,.m4a,.aac,.ogg,.wav,audio/*"
        style={{ display: "none" }}
        onChange={uploadAudio}
        data-testid="library-audio-file-input"
      />
      <div className="page-header" style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="page-title">{t("library.library")}</h1>
          <p className="page-subtitle">
            {t("library.curated_pdf_collection_sorted_by")}
          </p>
        </div>
        {isAdmin && (
          <button className="btn-primary-gold flex items-center gap-2" onClick={() => setShowForm(true)} data-testid="library-upload-btn">
            <Plus size={14} /> {t("library.upload_pdf")}
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <button
          className={`pill-tab ${activeCat === "all" ? "active" : ""}`}
          onClick={() => setActiveCat("all")}
          data-testid="library-cat-all"
        >
          {t("library.all")} <span className="pill-count">{counts.all || 0}</span>
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            className={`pill-tab ${activeCat === c ? "active" : ""}`}
            onClick={() => setActiveCat(c)}
            data-testid={`library-cat-${c}`}
          >
            {labels[c]} <span className="pill-count">{counts[c] || 0}</span>
          </button>
        ))}
      </div>

      <div className="gold-card mb-3" style={{ padding: "0.6rem 0.9rem", display: "flex", alignItems: "center", gap: 8 }}>
        <Search size={14} color="#9a7530" />
        <input
          className="input-lodge"
          style={{ border: "none", background: "transparent", flex: 1, padding: "0.3rem 0" }}
          placeholder={t("library.search_title_author_description")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="library-search"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="gold-card" style={{ padding: "2rem", textAlign: "center", color: "#9a7530", fontStyle: "italic" }} data-testid="library-empty">
          <BookOpen size={32} color="#c9a84c" style={{ margin: "0 auto 0.6rem", display: "block" }} />
          {t("library.no_books_in_this_section")}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {filtered.map((b) => (
            <div key={b.id} className="gold-card" style={{ padding: "1rem 1.1rem", display: "flex", flexDirection: "column", gap: 8 }} data-testid={`library-book-${b.id}`}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <BookOpen size={20} color="#c9a84c" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.95rem", lineHeight: 1.25 }}>
                    {b.title}
                  </div>
                  <div className="kv-label" style={{ fontSize: "0.65rem", marginTop: 2 }}>
                    {labels[b.category] || b.category}
                    {b.author && <> · {b.author}</>}
                  </div>
                </div>
              </div>
              {b.description && (
                <p style={{ color: "#34322d", fontSize: "0.85rem", margin: 0, lineHeight: 1.4 }}>{b.description}</p>
              )}
              {isAdmin && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.72rem" }}>
                  <Shield size={12} color="#9a7530" />
                  <span className="kv-label" style={{ fontSize: "0.66rem" }}>{t("library.min_degree")}:</span>
                  <select
                    className="input-lodge"
                    style={{ padding: "0.2rem 0.4rem", fontSize: "0.72rem", flex: 1 }}
                    value={b.min_grade || 1}
                    onChange={(e) => updateGrade(b, parseInt(e.target.value, 10))}
                    data-testid={`library-min-grade-${b.id}`}
                  >
                    {Array.from({ length: 33 }, (_, i) => i + 1).map((g) => (
                      <option key={g} value={g}>{DEGREE_NAMES[g]}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display: "flex", gap: 6, marginTop: "auto", flexWrap: "wrap" }}>
                <button className="btn-outline-gold flex items-center gap-1" style={{ flex: 1, minWidth: 110 }} onClick={() => onDownload(b)} data-testid={`library-download-${b.id}`}>
                  <Download size={12} /> {t("library.download")}
                </button>
                {isAdmin && (
                  <button
                    className="btn-outline-gold flex items-center gap-1"
                    style={{ minWidth: 110 }}
                    onClick={() => pickAudio(b)}
                    disabled={generatingAudioId === b.id}
                    title={b.audio_track_id ? (t("library.replace_audio")) : (t("library.upload_audio"))}
                    data-testid={`library-audio-upload-${b.id}`}
                  >
                    {generatingAudioId === b.id ? (
                      <Loader2 size={12} className="spin" />
                    ) : (
                      <Headphones size={12} />
                    )}
                    {b.audio_track_id ? (t("library.audio")) : t("library.upload_audio")}
                  </button>
                )}
                {isAdmin && b.audio_track_id && (
                  <button
                    className="btn-danger"
                    onClick={() => deleteAudio(b)}
                    title={t("library.delete_audio")}
                    data-testid={`library-audio-delete-${b.id}`}
                  >
                    <Headphones size={12} /> ✕
                  </button>
                )}
                {isAdmin && (
                  <button className="btn-danger" onClick={() => remove(b)} data-testid={`library-delete-${b.id}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && isAdmin && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content gold-card" style={{ maxWidth: 520, padding: "1.5rem" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>
                {t("library.upload_a_new_book")}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-500" data-testid="close-library-form">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="library-form">
              <div>
                <label className="label-lodge">{t("library.title")}</label>
                <input
                  className="input-lodge" required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="library-title"
                />
              </div>
              <div>
                <label className="label-lodge">{t("library.category")}</label>
                <select
                  className="input-lodge" required
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="library-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{labels[c]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-lodge">{t("library.author")}</label>
                <input
                  className="input-lodge"
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  data-testid="library-author"
                />
              </div>
              <div>
                <label className="label-lodge">{t("library.description")}</label>
                <textarea
                  className="input-lodge" rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="library-description"
                />
              </div>
              <div>
                <label className="label-lodge">{t("library.pdf_file")}</label>
                <FileInputLocalized
                  accept=".pdf,application/pdf"
                  required
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                  testId="library-file"
                />
              </div>
              {busy && progress > 0 ? (
                <div style={{ marginTop: "0.5rem" }}>
                  <div style={{ fontSize: "0.72rem", color: "#9a7530", marginBottom: 4 }}>{progressLabel}</div>
                  <div style={{ width: "100%", height: 6, background: "rgba(200,169,106,0.15)", borderRadius: 3 }}>
                    <div
                      style={{
                        width: `${progress}%`,
                        height: "100%",
                        background: "linear-gradient(90deg,#c9a84c,#e8c97a)",
                        borderRadius: 3,
                        transition: "width 0.2s",
                      }}
                      data-testid="library-upload-progress"
                    />
                  </div>
                </div>
              ) : null}
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)} disabled={busy}>
                  {t("library.cancel")}
                </button>
                <button type="submit" className="btn-primary-gold" disabled={busy} data-testid="library-submit">
                  {busy ? (progressLabel || "...") : (t("library.upload"))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
